import React, { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import styles from './CalendarView.module.scss';
import Badge from 'common/components/badge/Badge';
import { VehicleReservationDto } from 'api/vehicleReservations/models/VehicleReservationDto';
import { getReservationStatusBadgePreset } from 'common/utils/reservationStatus';
import { CalendarMode } from './CalendarView';

type Props = {
    date: Dayjs;
    items: VehicleReservationDto[];
    mode: CalendarMode;
    isCurrentMonth: boolean;
    isSelected: boolean;
    isToday: boolean;
    onClick: (date: Dayjs) => void;
};

const CalendarDayCell: React.FC<Props> = ({ date, items, mode, isCurrentMonth, isSelected, isToday, onClick }) => {
    const badges = useMemo(() => {
        if (items.length === 0) return null;

        if (mode === 'reservations') {
            return (
                <Badge
                    text={String(items.length)}
                    preset="primaryDark10"
                    size="round"
                    isSmallRound
                />
            );
        }

        if (mode === 'combined') {
            // Split into reservations (no status or ACTIVE) and pending requests
            const reservations = items.filter(i => !i.status || i.status === 'ACTIVE');
            const pendingRequests = items.filter(i => i.status === 'PENDING_APPROVAL');
            return (
                <>
                    {reservations.length > 0 && (
                        <Badge
                            text={String(reservations.length)}
                            preset="primaryDark10"
                            size="round"
                            isSmallRound
                        />
                    )}
                    {pendingRequests.length > 0 && (
                        <Badge
                            text={String(pendingRequests.length)}
                            preset="warning10"
                            size="round"
                            isSmallRound
                        />
                    )}
                </>
            );
        }

        // Requests mode: group by status
        const statusCounts: Record<string, number> = {};
        items.forEach(item => {
            const status = item.status || 'PENDING_APPROVAL';
            statusCounts[status] = (statusCounts[status] || 0) + 1;
        });

        const statuses = Object.keys(statusCounts);

        if (statuses.length === 1) {
            return (
                <Badge
                    text={String(items.length)}
                    preset={getReservationStatusBadgePreset(statuses[0])}
                    size="round"
                    isSmallRound
                />
            );
        }

        // Order: PENDING_APPROVAL, APPROVED, REJECTED
        const statusOrder = ['PENDING_APPROVAL', 'APPROVED', 'REJECTED'];
        return (
            <>
                {statusOrder.map(status => {
                    const count = statusCounts[status];
                    if (!count) return null;
                    return (
                        <Badge
                            key={status}
                            text={String(count)}
                            preset={getReservationStatusBadgePreset(status)}
                            size="round"
                            isSmallRound
                        />
                    );
                })}
            </>
        );
    }, [items, mode]);

    const cellClassName = [
        styles.dayCell,
        !isCurrentMonth && styles.dayCellOtherMonth,
        items.length > 0 && isCurrentMonth && styles.dayCellHasItems,
        isSelected && styles.dayCellSelected,
        isToday && styles.dayCellToday,
    ].filter(Boolean).join(' ');

    return (
        <div className={cellClassName} onClick={() => onClick(date)}>
            <span className={styles.dayNumber}>{date.date()}</span>
            {items.length > 0 && (
                <div className={styles.badgesRow}>
                    {badges}
                </div>
            )}
        </div>
    );
};

export default CalendarDayCell;
